import AddIcon from '@mui/icons-material/Add';
import ChevronRight from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ReactElement, ReactNode, useMemo, useState } from 'react';

import useFeature from '~/store/clinic/hooks/useFeature';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { formatMonth } from '~/utils/date/date';
import { useApiMutation, useApiRequest } from '~/hooks/api';
import { IClientDetailedV3 } from '~/services/api/client/types';
import { IArchivedMedicaidPlan } from '~/services/api/medicaidPlan/types';
import Button from '~/ui/components/common/Button';
import ListItem from '~/ui/components/common/ListItem';
import Loader from '~/ui/components/common/Loader';

import archiveIcon from '~/ui/assets/images/archiveIcon.svg';
import { ReactComponent as EditIcon } from '~/ui/assets/images/edit.svg';
import { getExpiresIn, getPaperworkStatus, getRecertificationColorByDate } from '../helpers';
import ArchiveMedicaidPlanModal from './components/ArchiveMedicaidPlanModal';
import CeChip from './components/CeChip';
import CeHistoryModal from './components/CeHistoryModal';
import MedicaidHistory from './components/MedicaidHistory';
import MedicaidHistoryModal from './components/MedicaidHistoryModal';
import MedicaidTypeLabel from './components/MedicaidTypeLabel';
import RecentMedicaidId from './components/RecentMedicaidId';
import { EDITS_DISABLED_TOOLTIP } from './constants';
import MedicaidPlanModal from './MedicaidPlanModal';

import variables from '~/ui/assets/styles/colors.module.scss';

import styles from './MedicaidPlan.module.scss';

interface IMedicaidPlanProps {
  client: IClientDetailedV3;
  clinicId: string;
  teamId: string;
  isLoading: boolean;
  onOpenCommunityEngagement: () => void;
}

const MedicaidPlan = ({
  client,
  clinicId,
  teamId,
  isLoading,
  onOpenCommunityEngagement,
}: IMedicaidPlanProps): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isCeHistoryOpen, setIsCeHistoryOpen] = useState(false);
  const [openedArchivedPlan, setOpenedArchivedPlan] = useState<IArchivedMedicaidPlan | null>(null);

  const { isMedicaidAvailable: hasExpansion } = useFeature();
  const prohibitEditsResponse = useStoreState(state => state.clinic.current?.prohibitEditsResponse);
  const plan = useStoreState(state => state.client.medicaidPlan);
  const history = useStoreState(state => state.client.medicaidHistory);
  const { onGetClient, onGetMedicaidHistory, setMedicaidPlan } = useStoreActions(
    actions => actions.client,
  );

  const requestInfo = useMemo(
    () => ({ clinicId, teamId, clientId: String(client.id) }),
    [clinicId, teamId, client.id],
  );

  const { isLoading: isHistoryLoading, refetch: refetchHistory } = useApiRequest(
    () => onGetMedicaidHistory(requestInfo),
    {
      enabled: hasExpansion,
      dependencies: [requestInfo],
    },
  );

  const { mutate: refreshClient } = useApiMutation<void, void>(() => onGetClient(requestInfo), {
    throwOnError: false,
  });

  const hasActions = !client.isArchived && !!prohibitEditsResponse && !isLoading;
  const areEditsDisabled = !!prohibitEditsResponse?.medicaid;

  const onSaved = () => {
    setIsModalOpen(false);
    return refreshClient();
  };

  const onArchived = async () => {
    setIsArchiveOpen(false);
    setMedicaidPlan(null);
    await Promise.all([refreshClient(), refetchHistory()]);
  };

  const recertificationDate = plan?.recertificationDate ?? null;
  const recertificationColor = getRecertificationColorByDate(
    recertificationDate,
    client.paperworkStatus,
  );
  const recertificationStyle =
    recertificationColor === variables.colorGrey ? undefined : { color: recertificationColor };

  const recertification = recertificationDate && (
    <div style={{ color: recertificationColor }}>
      {formatMonth(plan.recertificationDate)}
      {getExpiresIn(recertificationDate)}
      {!!client.paperworkStatus && (
        <>
          <br />
          <span className={styles.paperwork}>{getPaperworkStatus(client.paperworkStatus)}</span>
        </>
      )}
    </div>
  );

  const renderRows = (): ReactNode => {
    if (!plan) return null;

    const medicaidId = <div style={recertificationStyle}>{plan.medicaidId}</div>;
    if (!hasExpansion) {
      return (
        <>
          <ListItem label="Medicaid ID" content={medicaidId} moreLabelWidth />
          <ListItem label="Recertification Date" content={recertification} moreLabelWidth />
        </>
      );
    }

    const hasCeHistory = !plan.medicaidType?.isExpansion && plan.hasCommunityEngagementHistory;
    const type = plan.medicaidType && (
      <span className={styles.type}>
        <MedicaidTypeLabel type={plan.medicaidType} multiline />
        {hasCeHistory && (
          <button
            type="button"
            className={styles.ceHistoryLink}
            onClick={() => setIsCeHistoryOpen(true)}
          >
            View CE entries
            <ChevronRight aria-hidden className={styles.ceHistoryChevron} />
          </button>
        )}
      </span>
    );
    const effectiveDate = <span>{formatMonth(plan.effectiveDate)}</span>;

    return (
      <>
        {/* TODO: read the status from the plan once the response has it; the current plan is
            never archived, so it is Active until then. */}
        <ListItem
          label="Medicaid Status"
          content={<span className={styles.active}>Active</span>}
          moreLabelWidth
        />
        <ListItem label="Medicaid Type" content={type} moreLabelWidth />
        {plan.medicaidType?.isExpansion && (
          <ListItem
            label="Work / Engagement Hours"
            content={<CeChip client={requestInfo} onOpen={onOpenCommunityEngagement} />}
            moreLabelWidth
          />
        )}
        <ListItem label="Medicaid ID" content={medicaidId} moreLabelWidth />
        <ListItem label="Effective Date" content={effectiveDate} moreLabelWidth />
        <ListItem label="Recertification Date" content={recertification} moreLabelWidth />
      </>
    );
  };

  const renderActions = (): ReactNode => {
    if (!plan) {
      return (
        <Tooltip title={areEditsDisabled ? EDITS_DISABLED_TOOLTIP : ''} placement="top">
          <div className={styles.actions}>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              color="primary"
              disabled={areEditsDisabled}
              onClick={() => setIsModalOpen(true)}
            >
              Add Medicaid Plan
            </Button>
          </div>
        </Tooltip>
      );
    }
    if (areEditsDisabled) return null;
    return (
      <div className={styles.actions}>
        <Button
          startIcon={<EditIcon aria-hidden />}
          variant="outlined"
          color="primary"
          onClick={() => setIsModalOpen(true)}
        >
          Edit
        </Button>
        {hasExpansion && (
          <IconButton aria-label="Archive Medicaid Plan" onClick={() => setIsArchiveOpen(true)}>
            <img src={archiveIcon} alt="" />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBlock}>
        <div className={styles.heading}>
          <h4 className={styles.title}>Medicaid Plan</h4>
          {!plan && !isLoading && !!client.medicaId && (
            <RecentMedicaidId medicaidId={client.medicaId} />
          )}
        </div>
        {hasActions && renderActions()}
      </div>
      {isLoading ? <Loader /> : <div>{renderRows()}</div>}
      {hasExpansion && !isLoading && (
        <MedicaidHistory
          history={history}
          isLoading={isHistoryLoading}
          onOpen={setOpenedArchivedPlan}
        />
      )}
      {isModalOpen && (
        <MedicaidPlanModal
          plan={plan}
          hasExpansion={hasExpansion}
          isRecertificationRequired={client.medicaIdRequired || !!plan?.recertificationDate}
          requestInfo={requestInfo}
          onClose={() => setIsModalOpen(false)}
          onSaved={onSaved}
        />
      )}
      {isArchiveOpen && plan && (
        <ArchiveMedicaidPlanModal
          plan={plan}
          requestInfo={requestInfo}
          onClose={() => setIsArchiveOpen(false)}
          onArchived={onArchived}
        />
      )}
      {isCeHistoryOpen && (
        <CeHistoryModal client={requestInfo} onClose={() => setIsCeHistoryOpen(false)} />
      )}
      {openedArchivedPlan && (
        <MedicaidHistoryModal
          plan={openedArchivedPlan}
          client={requestInfo}
          onClose={() => setOpenedArchivedPlan(null)}
        />
      )}
    </div>
  );
};

export default MedicaidPlan;
